import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Megaphone, X } from 'lucide-react';
import axios from 'axios';
import authService from '../services/auth.service';
import { useCursorTopic } from '../utils/liveCursors';
import '../styles/AnnonceBandeau.css';

/**
 * Bandeau d'annonce persistant.
 * Chargé au démarrage (donc visible même si on n'était pas là à l'envoi)
 * et mis à jour en direct via Mercure, ou tout de suite dans le même
 * navigateur via l'événement `spot-bandeau`.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const DISMISS_KEY = 'spot-bandeau-dismiss';

const encoreValable = (data) => {
    if (!data?.actif || !data.message || !data.id) return false;
    const fin = new Date(data.visibleJusqua).getTime();
    return Number.isFinite(fin) && fin > Date.now();
};

const heureFin = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const aujourdHui = new Date();
    const memeJour = d.toDateString() === aujourdHui.toDateString();
    const heure = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    if (memeJour) return heure;
    const jour = d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    return `${jour} ${heure}`;
};

const AnnonceBandeau = () => {
    const [bandeau, setBandeau] = useState(null);
    const [dismissedId, setDismissedId] = useState(() => sessionStorage.getItem(DISMISS_KEY));

    const appliquer = useCallback((data) => {
        setBandeau(encoreValable(data) ? data : null);
    }, []);

    const charger = useCallback(() => {
        if (!authService.getCurrentUser()?.id) return;
        axios.get(`${API_URL}/annonce-bandeau`, { withCredentials: true })
            .then((res) => appliquer(res.data))
            .catch(() => {});
    }, [appliquer]);

    useEffect(() => {
        charger();
        const timer = setInterval(charger, 30000);
        return () => clearInterval(timer);
    }, [charger]);

    useEffect(() => {
        const onLocal = (e) => appliquer(e.detail);
        window.addEventListener('spot-bandeau', onLocal);
        return () => window.removeEventListener('spot-bandeau', onLocal);
    }, [appliquer]);

    useCursorTopic('spot/annonce-bandeau', appliquer, Boolean(authService.getCurrentUser()?.id));

    useEffect(() => {
        if (!bandeau?.visibleJusqua) return undefined;
        const reste = new Date(bandeau.visibleJusqua).getTime() - Date.now();
        if (reste <= 0) {
            setBandeau(null);
            return undefined;
        }
        const timer = setTimeout(() => setBandeau(null), reste);
        return () => clearTimeout(timer);
    }, [bandeau]);

    if (!bandeau || String(bandeau.id) === dismissedId) return null;

    const important = bandeau.niveau === 'important';
    const fin = heureFin(bandeau.visibleJusqua);

    const fermer = () => {
        sessionStorage.setItem(DISMISS_KEY, String(bandeau.id));
        setDismissedId(String(bandeau.id));
    };

    return (
        <div className={`anb${important ? ' anb--important' : ''}`} role="status">
            <span className="anb-icon">
                {important ? <AlertTriangle size={16} /> : <Megaphone size={16} />}
            </span>
            <p className="anb-message">{bandeau.message}</p>
            {fin && <span className="anb-fin">Jusqu'à {fin}</span>}
            <button type="button" className="anb-close" onClick={fermer} aria-label="Masquer le bandeau">
                <X size={15} />
            </button>
        </div>
    );
};

export default AnnonceBandeau;
